import {Component, Input, OnInit} from '@angular/core';
import {LoanRestructureService} from '../../service/loan-restructure.service';
import {WriteOffStatus, WriteOffTag} from '../../model/WriteOff';
import {WriteOffService} from '../../service/write-off.service';
import {EmiMoratorium} from '../../model/emi-moratorium.model';
import {ConfigService} from '../../service/config.service';
import {EmiMoratoriumService} from '../../service/emi-moratorium.service';
import {RestructuredLoanStatus} from '../../lender-delayed-loans/restructured-loans/restructured-loans.component';
import {Loan} from '../../model/loan.model';
import {size} from 'lodash';
import {LoanStatusType} from '../../model/loan-status.model';
import {LoanDues} from '../../model/loan-dues';
import moment = require('moment');

export interface LoanRestructureDetail {
    statusBeforeRestructure: string;
    principalOutstandingBeforeRestructure: number;
    principalWriteOffBeforeRestructure: number;
    oldTenure: number;
    oldEmi: number;
    oldRoi: number;
    restructuringDate: string;
    upfrontPayment: number;
    principalWriteOffOrWriteBack: number;
    capitalisedInterest: number;
    principalOutstandingPostRestructure: number;
    roiPostRestructure: number;
    tenurePostRestructure: number;
    emi: number;
    restructuredFirstEmiDate: string;

}

@Component({
    selector: 'app-restructured-loan-detail',
    templateUrl: './restructured-loan-detail.component.html',
    styleUrls: ['./restructured-loan-detail.component.scss']
})

export class RestructuredLoanDetailComponent implements OnInit {

    @Input()
    loan: Loan;
    @Input()
    lenderShare: number;
    public loanRestructureDetail: LoanRestructureDetail;

    private readonly loanRestructureService: LoanRestructureService;
    private readonly writeOffService: WriteOffService;
    private readonly configService: ConfigService;
    private readonly emiMoratoriumService: EmiMoratoriumService;

    constructor(loanRestructureService: LoanRestructureService, writeOffService: WriteOffService,
                configService: ConfigService, emiMoratoriumService: EmiMoratoriumService) {
        this.loanRestructureService = loanRestructureService;
        this.writeOffService = writeOffService;
        this.configService = configService;
        this.emiMoratoriumService = emiMoratoriumService;
    }

    async ngOnInit(): Promise<void> {
        this.loanRestructureDetail = await this.populateLoanRestructureDetail(this.loan._id);
    }

    private async populateLoanRestructureDetail(loanId: string): Promise<LoanRestructureDetail> {
        const loanRestructure = await this.loanRestructureService.getLatestRestructuringByLoanId(loanId);
        const delayedDaysBeforeRestructure = await this.getDelayedDaysBeforeRestructure(this.loan._id, loanRestructure.firstDelayedEmiDueOn, loanRestructure.restructuredOn);
        return <LoanRestructureDetail>{
            statusBeforeRestructure: this.getStatusBeforeRestructure(delayedDaysBeforeRestructure),
            principalOutstandingBeforeRestructure: this.loanRestructureService.getPrincipalOutstandingBeforeRestructuringExcludingWriteOff(loanRestructure.duesBeforeRestructure) * this.lenderShare,
            oldTenure: loanRestructure.oldTenureInMonths,
            oldEmi: loanRestructure.oldEmiAmount * this.lenderShare,
            oldRoi: loanRestructure.oldYearlyRoiInPercentage,
            restructuringDate: moment(loanRestructure.restructuredOn).format('DD MMM YYYY'),
            upfrontPayment: loanRestructure.upfrontPaymentTotal * this.lenderShare,
            principalWriteOffOrWriteBack: await this.getWrittenOffPrincipal(this.loan._id) * this.lenderShare,
            restructuredFirstEmiDate: moment(loanRestructure.firstEmiDueDate).format('DD MMM YYYY'),
            tenurePostRestructure: loanRestructure.tenureInMonths,
            roiPostRestructure: loanRestructure.yearlyRoiInPercentage,
            capitalisedInterest: (loanRestructure.capitalizedInterestInPrincipal + loanRestructure.capitalizedInterestRestructure) * this.lenderShare,
            emi: this.loan.emi_schedule[size(this.loan.emi_schedule) - 1].emi_amount * this.lenderShare,
            principalOutstandingPostRestructure: this.getOutstandingPrincipal(this.loan) * this.lenderShare,
            principalWriteOffBeforeRestructure: this.getWrittenOffPrincipalBeforeRestructuring(loanRestructure.duesBeforeRestructure) * this.lenderShare
        };
    }

    private async getWrittenOffPrincipal(loanId) {
        const writeOff = await this.writeOffService.getWriteOffByLoanId(loanId);
        if (!writeOff) {
            return 0;
        }
        const restructureDetails = writeOff.history.find(history => history.tag === WriteOffTag.LoanRestructure);
        if (!restructureDetails) {
            return 0;
        }
        return restructureDetails.status === WriteOffStatus.WriteOff ? -restructureDetails.principal : restructureDetails.principal;
    }

    private getStatusBeforeRestructure(noOfDelayedDays) {
        const npaDays = this.configService.getNpaDays();
        const substandardAssetThresholdDays = this.configService.getSubstandardAssetWriteOffThresholdInDays();
        const lossAssetThresholdDays = this.configService.getLossAssetWriteOffThresholdInDays();

        if (noOfDelayedDays === 0) {
            return RestructuredLoanStatus.Regular;
        } else if (noOfDelayedDays < npaDays) {
            return RestructuredLoanStatus.Delayed;
        } else if (noOfDelayedDays >= npaDays && noOfDelayedDays < substandardAssetThresholdDays) {
            return RestructuredLoanStatus.Npa;
        } else if (noOfDelayedDays >= substandardAssetThresholdDays && noOfDelayedDays < lossAssetThresholdDays) {
            return RestructuredLoanStatus.SubStandAsset;
        } else {
            return RestructuredLoanStatus.LossAsset;
        }
    }

    private async getDelayedDaysBeforeRestructure(loanId: string, firstDelayedEmiDueOn: Date, restructuredOn: Date): Promise<number> {
        let noOfDelayedDays = 0, moratoriumDays = 0;
        if (!firstDelayedEmiDueOn) {
            return noOfDelayedDays;
        } else {
            const emiMoratorium = await this.getEmiMoratorium(loanId);
            if (emiMoratorium && firstDelayedEmiDueOn < emiMoratorium.startsAt) {
                moratoriumDays = moment(emiMoratorium.startsAt).endOf('day')
                    .diff(moment(emiMoratorium.endsAt).endOf('month').toDate());
            }
            noOfDelayedDays = moment(restructuredOn).endOf('day').diff(moment(firstDelayedEmiDueOn).endOf('day'), 'days');
            return noOfDelayedDays - moratoriumDays;
        }

    }

    private async getEmiMoratorium(loanId: string): Promise<EmiMoratorium> {
        const emiMoratoriums = await this.emiMoratoriumService.getEmiMoratoriumsByLoanId(loanId);
        return emiMoratoriums && emiMoratoriums.find(emiMoratorium => emiMoratorium.deletedAt === undefined);
    }

    // TODO: Duplicate Code
    private getOutstandingPrincipal(loan: Loan) {
        if ([LoanStatusType.ClosedSettlement, LoanStatusType.LoanMatured, LoanStatusType.ClosedPrepayment].includes(loan.loan_sanction_status)) {
            return 0;
        }
        const delayedEmis = loan.emi_schedule.filter(emi => !['emi_received', 'emi_completed'].includes(emi.status));
        const firstDelayedEmi = delayedEmis.find(emi => !!emi);
        return firstDelayedEmi.balance_amount + firstDelayedEmi.principal_repayment;
    }

    private getWrittenOffPrincipalBeforeRestructuring(duesBeforeRestructure: LoanDues): number {
        let writtenOffPrincipal = 0;
        if (duesBeforeRestructure.writeOff && duesBeforeRestructure.writeOff.history.length > 0) {
            let i = duesBeforeRestructure.writeOff.history.length - 1;
            while (i >= 0 && duesBeforeRestructure.writeOff.history[i].tag !== WriteOffTag.LoanRestructure) {
                writtenOffPrincipal += duesBeforeRestructure.writeOff.history[i].principal;
                i--;
            }
        }
        return writtenOffPrincipal;
    }

}
